import {
  Icon,
  iconVariants,
  SearchOutlineIcon,
  XOutlineIcon,
} from '@fountain/ripple/icon';
import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import messages from './messages';
import { useStyles } from './styles';

/**
 * Long enough that a typed word is one request rather than five, short enough
 * that the list feels answerable. Not configurable — one number, stated once.
 */
const DEBOUNCE_MS = 300;

const FIELD_ID = 'approvals-queue-search';

interface QueueSearchFieldProps {
  /** The term the list is currently filtered by. */
  value: string;
  onChange: (next: string) => void;
}

/**
 * Free-text search over the queue (Design Requirements §5).
 *
 * ## Debounced, and the page reset is not
 *
 * The field holds what has been typed and emits it after a pause, so a word is
 * one request rather than one per keystroke. What it does NOT own is the page
 * reset that goes with a new term: the caller does that synchronously when the
 * debounced value arrives. An earlier draft armed the reset inside the timer,
 * which meant pressing Next and then Clear moved the reader back a page up to
 * 300ms after they had already turned it.
 *
 * Clear is immediate and cancels anything pending. A person who presses it has
 * decided; making them wait out a timer they cannot see is the one case where the
 * debounce is only ever wrong.
 *
 * ## It follows an external reset
 *
 * The filtered-empty state's "Clear filters" also clears the term, so the field
 * cannot be the only place the value lives. It syncs from `value` whenever that
 * changes to something the field did not itself emit — which is what keeps a
 * controlled input from fighting the person typing into it.
 */
export const QueueSearchField: React.FC<QueueSearchFieldProps> = ({
  value,
  onChange,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const [text, setText] = useState(value);
  const emitted = useRef(value);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value !== emitted.current) {
      // Disarmed as well as synced. A keystroke arms a timer closing over what
      // was typed; a reset that arrives from the parent inside that window would
      // otherwise clear the box and then have the timer fire the term straight
      // back in, leaving the list filtered by something the field no longer
      // shows and nothing to correct it until somebody types again.
      clearTimeout(timer.current);
      emitted.current = value;
      setText(value);
    }
  }, [value]);

  // On unmount as well as between keystrokes: a pending timer that fires into a
  // container that is gone sets state on nothing.
  useEffect(() => () => clearTimeout(timer.current), []);

  const emit = (next: string) => {
    emitted.current = next;
    onChange(next);
  };

  const handleType = (next: string) => {
    setText(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => emit(next.trim()), DEBOUNCE_MS);
  };

  const handleClear = () => {
    clearTimeout(timer.current);
    setText('');
    emit('');
    // The clear button sits in the field's own endAdornment, which unmounts
    // the moment `text` goes empty — taking whatever had focus with it. No
    // deferral needed here the way a removed row needs one: the input is
    // never unmounted, so moving focus onto it now, before that re-render
    // even happens, means the button is no longer the focused element by
    // the time it disappears — the browser has nothing to drop to `<body>`.
    inputRef.current?.focus();
  };

  return (
    <TextField
      className={styles.searchField}
      // Explicit, because MUI v4 mints no id of its own: without one the
      // placeholder is the field's only name, and a placeholder is not one.
      id={FIELD_ID}
      inputRef={inputRef}
      inputProps={{ 'aria-label': intl.formatMessage(messages.searchLabel) }}
      placeholder={intl.formatMessage(messages.searchPlaceholder)}
      size="small"
      value={text}
      variant="outlined"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {/* Decorative: the field is named by its `aria-label`, and a second
                announcement of "search" is noise. */}
            <SearchOutlineIcon
              aria-hidden="true"
              className={classNames(
                iconVariants({ size: 'lg' }),
                styles.searchIcon,
              )}
            />
          </InputAdornment>
        ),
        // Only once there is something to clear — an always-present x on an
        // empty field is a control that does nothing.
        endAdornment: text !== '' && (
          <InputAdornment position="end">
            <IconButton
              // `clearSearch`, not the generic `clear`: the shared file already
              // carries "Clear search" for exactly this control, and the bare
              // verb loses which box a screen-reader user is emptying.
              aria-label={intl.formatMessage(globalMessages.clearSearch)}
              className={styles.searchClear}
              size="small"
              onClick={handleClear}
            >
              <Icon icon={XOutlineIcon} size="lg" color="inherit" />
            </IconButton>
          </InputAdornment>
        ),
      }}
      onChange={event => handleType(event.target.value)}
    />
  );
};
